/**
 * ⚡ REQUEST PRIORITIZATION
 * Ensures critical requests load first, non-critical requests are deferred
 */

import { logger } from './logger';

// TypeScript declarations for requestIdleCallback
declare global {
  interface Window {
    requestIdleCallback: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    cancelIdleCallback: (handle: number) => void;
  }
}

interface IdleRequestOptions {
  timeout?: number;
}

type IdleRequestCallback = (deadline: IdleDeadline) => void;

interface IdleDeadline {
  readonly didTimeout: boolean;
  timeRemaining(): number;
}

export enum RequestPriority {
  CRITICAL = 'critical',    // Dashboard data, authentication
  HIGH = 'high',           // Expenses, balances, groups
  NORMAL = 'normal',       // Settlements, user data
  LOW = 'low',             // Analytics, insights
  IDLE = 'idle'            // Background tasks, prefetching
}

/**
 * Defer a low-priority request until the browser is idle
 */
export const deferUntilIdle = (callback: () => void, timeout: number = 2000): number => {
  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    return window.requestIdleCallback(callback, { timeout });
  } else {
    // Fallback for browsers that don't support requestIdleCallback
    return setTimeout(callback, timeout) as unknown as number;
  }
};

/**
 * Cancel a deferred request
 */
export const cancelDeferred = (id: number | null) => {
  if (id === null) return;
  
  if (typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(id);
  } else {
    clearTimeout(id);
  }
};

/**
 * Wrap a low-priority API call with idle callback
 */
export const wrapLowPriorityRequest = async <T>(
  requestFn: () => Promise<T>,
  priority: RequestPriority = RequestPriority.LOW
): Promise<T> => {
  if (priority === RequestPriority.CRITICAL || priority === RequestPriority.HIGH) {
    // Execute immediately
    return requestFn();
  }

  // Defer until idle
  return new Promise((resolve, reject) => {
    deferUntilIdle(async () => {
      try {
        const result = await requestFn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  });
};

/**
 * Get priority headers for fetch requests
 */
export const getPriorityHeaders = (priority: RequestPriority): Record<string, string> => {
  // Note: Priority hints are experimental (Chrome only)
  // https://developer.chrome.com/blog/new-in-chrome-101/#priority-hints
  const headers: Record<string, string> = {};
  
  switch (priority) {
    case RequestPriority.CRITICAL:
    case RequestPriority.HIGH:
      headers['importance'] = 'high';
      break;
    case RequestPriority.LOW:
    case RequestPriority.IDLE:
      headers['importance'] = 'low';
      break;
    default:
      headers['importance'] = 'auto';
  }
  
  return headers;
};

/**
 * Batch multiple low-priority requests together
 */
export class RequestQueue {
  private queue: Array<() => Promise<any>> = [];
  private processing = false;
  private maxConcurrent = 2; // Only 2 low-priority requests at a time

  add(requestFn: () => Promise<any>) {
    this.queue.push(requestFn);
    this.process();
  }

  private async process() {
    if (this.processing || this.queue.length === 0) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      // Process up to maxConcurrent requests in parallel
      const batch = this.queue.splice(0, this.maxConcurrent);
      
      try {
        await Promise.all(batch.map(fn => fn()));
      } catch (error) {
        logger.error('Request queue error:', error);
      }

      // Small delay between batches to avoid overwhelming the browser
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.processing = false;
  }

  clear() {
    this.queue = [];
  }
}

// Global request queue for low-priority requests
export const lowPriorityQueue = new RequestQueue();

logger.debug('⚡ Request prioritization initialized');

